import { useEffect, useMemo, useState } from 'react';

import {
  Copy01Icon,
  Delete02Icon,
  PencilEdit01Icon,
  Settings01Icon,
} from '@hugeicons/core-free-icons';
import { EMAIL_TEMPLATE_TYPE_OPTIONS } from 'constants/email-config';
import { useToast } from 'hooks/toast';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useDispatch, useSelector } from 'react-redux';

import Alert from 'components/Alert/Alert';
import { EmailTemplateModal } from 'components/EmailTemplateModal/EmailTemplateModal';
import { Button, SearchInput, Select } from 'components/FormComponents';
import { IconButton } from 'components/IconButton/IconButton';
import StatusPill from 'components/Pills/StatusPills';
import { Table } from 'components/Table/Table';

import { getBrands, selectBrands } from 'api/brands-slice';
import {
  useCreateEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
  useDuplicateEmailTemplateMutation,
  useGetEmailTemplatesQuery,
  useUpdateEmailTemplateMutation,
} from 'api/email-templates-api';

// Future: team-scoped email templates — restore getTeams/selectTeams and team filter + column below.
// import { getTeams, selectTeams } from 'api/teams-slice';

const CATEGORY_DOT_COLOR = {
  candidate: 'bg-green-2',
  referee: 'bg-yellow-2',
};

export const EmailTemplates = () => {
  const { t } = useTranslation();
  const showToast = useToast();
  const dispatch = useDispatch();
  // const teams = useSelector(selectTeams);
  const brands = useSelector(selectBrands);

  const [categoryFilter, setCategoryFilter] = useState('');
  // const [teamFilter, setTeamFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (brands && brands?.length === 0) {
      dispatch(getBrands());
    }
  }, [dispatch]);

  const queryParams = useMemo(() => {
    const p = {};
    if (categoryFilter) p.category = categoryFilter;
    // if (teamFilter === 'global') p.team_id = 'global';
    // else if (teamFilter) p.team_id = teamFilter;
    if (searchQuery.trim()) p.search = searchQuery.trim();
    return p;
  }, [categoryFilter, searchQuery]);

  const { data: templates = [], isLoading, isFetching } = useGetEmailTemplatesQuery(queryParams);
  const [createEmailTemplate, { isLoading: isCreating }] = useCreateEmailTemplateMutation();
  const [updateEmailTemplate, { isLoading: isUpdating }] = useUpdateEmailTemplateMutation();
  const [deleteEmailTemplate] = useDeleteEmailTemplateMutation();
  const [duplicateEmailTemplate] = useDuplicateEmailTemplateMutation();

  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deletingTemplateId, setDeletingTemplateId] = useState(null);

  // useEffect(() => {
  //   dispatch(getTeams());
  // }, [dispatch]);

  const hasActiveFilters = Boolean(categoryFilter || searchQuery.trim());

  const openCreate = () => {
    setEditingTemplate(null);
    setModalVisible(true);
  };

  const openEdit = template => {
    setEditingTemplate(template);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingTemplate(null);
  };

  const handleSubmit = async data => {
    let action;
    if (editingTemplate?.is_system_default) {
      action = createEmailTemplate({ ...data, system_default_id: editingTemplate.id });
    } else if (editingTemplate) {
      action = updateEmailTemplate({ id: editingTemplate.id, ...data });
    } else {
      action = createEmailTemplate(data);
    }

    const result = await action;
    if (result.error) {
      showToast(t('common.error'), t('emailTemplates.errors.saveFailed'), 'error');
      return;
    }
    closeModal();
  };

  const handleDuplicate = async id => {
    const result = await duplicateEmailTemplate(id);
    if (result.error) {
      showToast(t('common.error'), t('emailTemplates.errors.duplicateFailed'), 'error');
    }
  };

  const handleDelete = async () => {
    const result = await deleteEmailTemplate(deletingTemplateId);
    if (result.error) {
      showToast(t('common.error'), t('emailTemplates.errors.deleteFailed'), 'error');
    }
    setDeletingTemplateId(null);
  };

  // const teamLabel = template => {
  //   if (template.team_id == null) return t('emailTemplates.filters.companyWide');
  //   return teams?.find(team => team.id == template.team_id)?.name ?? '—';
  // };

  const eventTypeLabel = template => {
    const opt = EMAIL_TEMPLATE_TYPE_OPTIONS.find(o => o.value === template.type);
    return opt?.label ?? '—';
  };

  const brandRefMatchesTemplate = (ref, template) => {
    if (ref == null || ref === '') return false;
    const tid = template.id;
    const isDefault = template.is_system_default;
    if (typeof ref === 'string' && /^(company|default)-\d+$/.test(ref)) {
      if (ref === `company-${tid}`) return !isDefault;
      if (ref === `default-${tid}`) return isDefault;
      return false;
    }
    return Number(ref) === Number(tid);
  };

  const attachedToBrand = template => {
    return brands?.some(
      brand =>
        brandRefMatchesTemplate(brand?.email_candidate_template_id, template) ||
        brandRefMatchesTemplate(brand?.email_referee_template_id, template) ||
        brandRefMatchesTemplate(brand?.email_candidate_portal_template_id, template),
    );
  };

  const showInitialEmpty = !isLoading && templates.length === 0 && !hasActiveFilters;
  const showFilteredEmpty = !isLoading && templates.length === 0 && hasActiveFilters;

  return (
    <div className="bg-neutral-2 rounded-3xl shadow-sm border border-neutral-4 overflow-hidden">
      <Alert
        show={!!deletingTemplateId}
        title={t('emailTemplates.delete.title')}
        message={t('emailTemplates.delete.message')}
        ok={t('common.delete')}
        cancel={t('common.cancel')}
        type="error"
        onOk={handleDelete}
        onCancel={() => setDeletingTemplateId(null)}
      />

      <EmailTemplateModal
        visible={modalVisible}
        onClose={closeModal}
        onSubmit={handleSubmit}
        initialValues={editingTemplate}
        isLoading={isCreating || isUpdating}
      />

      <div className="flex flex-col gap-6 bg-neutral-1 p-8 border-b border-neutral-4">
        <div className="flex justify-between items-start">
          <div>
            <span className="text-2xl font-semibold text-neutral-7 mb-1">
              {t('emailTemplates.title')}
            </span>
            <p className="text-xl text-neutral-6">{t('emailTemplates.subtitle')}</p>
          </div>
          <Button type="primary" onClick={openCreate}>
            {t('emailTemplates.createButton')}
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            placeholder={t('emailTemplates.filters.searchPlaceholder')}
            value={searchQuery}
            onChange={setSearchQuery}
            className="min-w-[200px] flex-1 max-w-md"
          />
          <Select
            name="emailTemplateCategoryFilter"
            className="w-48 min-w-[12rem]"
            value={categoryFilter}
            inputProps={{ onChange: e => setCategoryFilter(e.target.value) }}
            placeholder={t('emailTemplates.filters.allCategories')}>
            <Select.Item value="">{t('emailTemplates.filters.allCategories')}</Select.Item>
            <Select.Item value="candidate">{t('emailTemplates.category.candidate')}</Select.Item>
            <Select.Item value="referee">{t('emailTemplates.category.referee')}</Select.Item>
          </Select>
          {/* Future: team filter (see teams-slice import above) */}
        </div>
      </div>

      {(isLoading || isFetching) && templates.length === 0 && (
        <p className="text-sm text-neutral-5">{t('common.loading')}</p>
      )}

      {showInitialEmpty && (
        <p className="text-sm text-neutral-5 mt-4">{t('emailTemplates.empty')}</p>
      )}

      {showFilteredEmpty && (
        <p className="text-sm text-neutral-5 mt-4">{t('emailTemplates.noResults')}</p>
      )}

      {!isLoading && templates.length > 0 && (
        <>
          <div className="p-8">
            <Table.Context>
              <Table.TableContent className="overflow-x-auto">
                <Table>
                  <Table.Head>
                    <tr>
                      <Table.Header noBorder>{t('emailTemplates.table.name')}</Table.Header>
                      <Table.Header noBorder>{t('emailTemplates.table.audience')}</Table.Header>
                      <Table.Header noBorder>{t('emailTemplates.table.event')}</Table.Header>
                      {/* <Table.Header noBorder>{t('emailTemplates.table.team')}</Table.Header> */}
                      <Table.Header noBorder>{t('emailTemplates.table.updated')}</Table.Header>
                      <Table.Header noBorder align="end">
                        {t('emailTemplates.table.actions')}
                      </Table.Header>
                    </tr>
                  </Table.Head>
                  <Table.Body>
                    {templates.map(template => {
                      const isTemplateAttachedToBrand = attachedToBrand(template);
                      return (
                        <Table.Row
                          key={
                            template.is_system_default
                              ? `default-${template.id}`
                              : `company-${template.id}`
                          }>
                          <Table.Col className="text-xl font-medium text-neutral-7">
                            <div className="flex flex-col">
                              <div className="flex items-center gap-2">
                                {template.name}
                                {template.is_system_default && (
                                  <StatusPill
                                    status="info"
                                    text={t('emailTemplates.preloadedTemplate')}
                                    className="px-4"
                                  />
                                )}
                              </div>
                              <span className="text-lg font-medium text-text-tertiary max-w-lg truncate inline-block align-bottom">
                                Subject: {template.subject}
                              </span>
                            </div>
                          </Table.Col>
                          <Table.Col>
                            <StatusPill
                              status="neutral"
                              text={
                                template.category === 'candidate'
                                  ? t('emailTemplates.category.candidate')
                                  : t('emailTemplates.category.referee')
                              }
                              colorDot={CATEGORY_DOT_COLOR[template.category] || 'bg-neutral-4'}
                              className="px-4"
                            />
                          </Table.Col>
                          <Table.Col>
                            <span className="text-xl font-medium text-neutral-6">
                              {eventTypeLabel(template)}
                            </span>
                          </Table.Col>
                          {/* <Table.Col className="text-xl font-medium text-neutral-6">
                            {teamLabel(template)}
                          </Table.Col> */}
                          <Table.Col>
                            <span className="text-xl font-medium text-neutral-6 whitespace-nowrap">
                              <Moment fromNow>{template.updated_at || template.created_at}</Moment>
                            </span>
                          </Table.Col>
                          <Table.Col align="right">
                            <div className="d-flex align-items-center justify-content-end">
                              {template.is_system_default ? (
                                <IconButton
                                  icon={Settings01Icon}
                                  prefix="hugeicons"
                                  tip={t('emailTemplates.customize')}
                                  onClick={() => {
                                    openEdit(template);
                                  }}
                                  placement="top"
                                />
                              ) : (
                                <>
                                  <IconButton
                                    className="u-margin-right--small"
                                    icon={PencilEdit01Icon}
                                    prefix="hugeicons"
                                    tip={t('common.edit')}
                                    onClick={() => openEdit(template)}
                                    placement="top"
                                  />
                                  <IconButton
                                    className="u-margin-right--small"
                                    icon={Copy01Icon}
                                    prefix="hugeicons"
                                    tip={t('emailTemplates.table.duplicate')}
                                    onClick={() => handleDuplicate(template.id)}
                                    placement="top"
                                  />
                                  <IconButton
                                    icon={Delete02Icon}
                                    prefix="hugeicons"
                                    tip={
                                      isTemplateAttachedToBrand
                                        ? 'This template is attached to a brand and cannot be deleted'
                                        : t('common.delete')
                                    }
                                    onClick={() => setDeletingTemplateId(template.id)}
                                    placement="top"
                                    type="delete"
                                    disabled={isTemplateAttachedToBrand}
                                  />
                                </>
                              )}
                            </div>
                          </Table.Col>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>
              </Table.TableContent>
            </Table.Context>
          </div>
        </>
      )}
    </div>
  );
};
